import { useContext } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

import { ReactComponent as CheckCircleIcon } from "assets/images/iconsV2/check-circle.svg";
import { ReactComponent as EyeIcon } from "assets/images/iconsV2/eye.svg";
import Button from "components/v2/Button/Button";
import NavContext from "components/molecules/Navigation/components/common/NavContext";
import testIds from "constants/testIds";
import { classNames } from "helpers/commonHelpers";
import { useBottomBannerOffset } from "@RequestFormV2/hooks/useBottomBannerOffset";
import { trackQuoteDraftSaved } from "@RequestFormV2/formEvents/requestFormAnalytics";

interface RequestFormFooterProps {
  isReviewMode: boolean;
  hasPaymentLocation: boolean;
  isSubmitting: boolean;
  isReviewValidating?: boolean;
  currentSection?: string;
  onReviewClick: () => void;
  onEditClick: () => void;
  onSubmitClick: () => void;
  onSaveDraftClick: () => Promise<boolean>;
}

export default function RequestFormFooter({
  isReviewMode,
  hasPaymentLocation,
  isSubmitting,
  isReviewValidating = false,
  currentSection = "request-form.organisation",
  onReviewClick,
  onEditClick,
  onSubmitClick,
  onSaveDraftClick,
}: RequestFormFooterProps) {
  const intl = useIntl();
  const { navigationConfig } = useContext(NavContext);
  const bottomOffset = useBottomBannerOffset();

  const handleSaveDraftClick = async () => {
    const saved = await onSaveDraftClick();
    if (saved) {
      trackQuoteDraftSaved(currentSection);
      toast(intl.formatMessage({ id: "request-form.header.draftSaved" }), { type: "success" });
    }
  };

  return (
    <div
      className={classNames("rfv2-footer", {
        "rfv2-footer--expanded": navigationConfig?.expanded,
        "rfv2-footer--review": isReviewMode,
      })}
      style={{ bottom: bottomOffset }}
    >
      <div className="rfv2-footer__content">
        {!isReviewMode && (
          <p className="rfv2-footer__hint">
            <span className="rfv2-footer__hint-asterisk">*</span>
            {intl.formatMessage({ id: "request-form.footer.requiredFieldsHint" })}
          </p>
        )}
        <div className="rfv2-footer__actions">
          {isReviewMode ? (
            <>
              <span data-testid={testIds.REQUEST_FORM_REVIEW_BACK_TO_EDITING}>
                <Button
                  variant="secondary-gray"
                  size="md"
                  label={intl.formatMessage({ id: "request-form.footer.goToEdit" })}
                  onClick={onEditClick}
                />
              </span>
              <span data-testid={testIds.REQUEST_FORM_REVIEW_SUBMIT}>
                <Button
                  variant="primary"
                  size="md"
                  label={intl.formatMessage({
                    id: isSubmitting ? "general.submitting" : "general.submit",
                  })}
                  iconLeft={<CheckCircleIcon />}
                  onClick={onSubmitClick}
                  disabled={isSubmitting}
                />
              </span>
            </>
          ) : (
            <>
              <span data-testid={testIds.REQUEST_FORM_FOOTER_SAVE_DRAFT}>
                <Button
                  variant="secondary-gray"
                  size="md"
                  label={intl.formatMessage({ id: "request-form.footer.saveDraft" })}
                  disabled={!hasPaymentLocation}
                  onClick={() => {
                    handleSaveDraftClick().catch(() => undefined);
                  }}
                />
              </span>
              <span data-testid={testIds.REQUEST_FORM_FOOTER_REVIEW}>
                <Button
                  variant="primary"
                  size="md"
                  label={intl.formatMessage({ id: "general.review" })}
                  iconLeft={<EyeIcon />}
                  onClick={onReviewClick}
                  disabled={!hasPaymentLocation || isReviewValidating}
                />
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
